import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { ArrowLeft, UserPlus, Pencil, Users, FolderKanban, Smartphone, CalendarDays, AlertTriangle, Trash2, ShieldCheck } from 'lucide-react';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { TenantCommercial } from '@/components/platform/TenantCommercial';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { formatMoney } from '@/lib/money';

interface TenantRow { id: string; name: string; currency: string; plan: string; numbers: number; billing_exempt: boolean; created_at: string; deletion_pending?: boolean; deletion_run_after?: string; plan_comp?: boolean; billing_exempt_until?: string; plan_renews_at?: string }
interface DayPoint { date: string; sent: number; received: number }
interface Usage { total: number; sent: number; received: number; delivered: number; read: number; delivery_rate: number; series?: DayPoint[] }
interface CampaignConfig { warmup_days: number; min_numbers: number }
interface Detail { tenant: TenantRow; counts: { users: number; projects: number; numbers: number }; usage: Usage; campaign_config?: CampaignConfig }
interface AccUser { id: string; email: string; name: string; role: string; created_at: string; email_verified_at?: string | null }
interface Invoice { id: string; number: number; currency: string; status: string; reason: string; total_cents: number; period_start?: string; period_end?: string; issue_date: string; paid_at?: string }

const PLAN_LABEL: Record<string, string> = { pro: 'Pro', free: 'Free', exempt: 'Cortesia' };
const INV_STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  paid: { label: 'Paga', variant: 'success' },
  open: { label: 'Aberta', variant: 'warning' },
  awaiting_payment: { label: 'Aguardando', variant: 'warning' },
  failed: { label: 'Falhou', variant: 'destructive' },
  void: { label: 'Anulada', variant: 'secondary' },
};
const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// CampaignConfigCard — política de envio de campanha da conta, editável SÓ pelo admin
// da plataforma: dias de aquecimento (idade mín. de um número p/ entrar em campanha)
// e nº mínimo de números elegíveis p/ disparar. Padrões: 7 dias, 2 números.
function CampaignConfigCard({ id, initial, onSaved }: { id: string; initial: CampaignConfig; onSaved: () => void }) {
  const [warmup, setWarmup] = useState(String(initial.warmup_days));
  const [minNum, setMinNum] = useState(String(initial.min_numbers));
  const [saving, setSaving] = useState(false);
  const dirty = warmup !== String(initial.warmup_days) || minNum !== String(initial.min_numbers);
  const save = async () => {
    setSaving(true);
    try {
      const r = await apiFetch('PATCH', `/platform/tenants/${id}/campaign-config`, {
        warmup_days: Math.max(0, Math.min(30, parseInt(warmup, 10) || 0)),
        min_numbers: Math.max(1, Math.min(10, parseInt(minNum, 10) || 1)),
      });
      if (!r.ok) { toast.error('Falha ao salvar a configuração de campanha.'); return; }
      const cc = r.data as CampaignConfig;
      setWarmup(String(cc.warmup_days)); setMinNum(String(cc.min_numbers));
      toast.success('Configuração de campanha salva.');
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <Card className="min-w-0">
      <CardHeader className="flex-row items-center gap-2">
        <ShieldCheck className="size-4 text-primary" />
        <CardTitle>Campanhas (política de envio)</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Política de aquecimento desta conta. Definida <strong>só por você</strong> — o cliente não edita, apenas vê o status dos números dele.
        </p>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="cc-warmup">Dias de aquecimento</Label>
            <Input id="cc-warmup" type="number" min={0} max={30} value={warmup} onChange={(e) => setWarmup(e.target.value)} />
            <p className="text-xs text-muted-foreground">Idade mínima de um número para entrar em campanha. <strong>0</strong> libera na hora (teste). Padrão: 7.</p>
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="cc-min">Mínimo de números</Label>
            <Input id="cc-min" type="number" min={1} max={10} value={minNum} onChange={(e) => setMinNum(e.target.value)} />
            <p className="text-xs text-muted-foreground">Nº de números elegíveis exigidos para uma campanha disparar. Padrão: 2.</p>
          </div>
        </div>
        <Button className="self-start" onClick={save} disabled={saving || !dirty}>Salvar configuração</Button>
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | number }) {
  return (
    <Card><CardContent className="flex items-center gap-3 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10"><Icon className="size-4 text-primary" /></div>
      <div className="min-w-0">
        <p className="truncate text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-lg font-bold">{value}</p>
      </div>
    </CardContent></Card>
  );
}

export function PlatformTenantDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [delOpen, setDelOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['platform-tenant', id],
    queryFn: async (): Promise<Detail | null> => {
      const r = await apiFetch('GET', `/platform/tenants/${id}`);
      return r.ok ? (r.data as Detail) : null;
    },
  });
  const users = useQuery({
    queryKey: ['platform-tenant-users', id],
    queryFn: async (): Promise<AccUser[]> => ((await apiFetch('GET', `/platform/tenants/${id}/users`)).data as { data?: AccUser[] })?.data ?? [],
  });
  const invoices = useQuery({
    queryKey: ['platform-tenant-invoices', id],
    queryFn: async (): Promise<Invoice[]> => ((await apiFetch('GET', `/platform/tenants/${id}/invoices`)).data as { data?: Invoice[] })?.data ?? [],
  });

  // Convite (cadastrar usuário).
  const [invEmail, setInvEmail] = useState('');
  const [invName, setInvName] = useState('');
  const [invRole, setInvRole] = useState('agent');
  const [inviting, setInviting] = useState(false);
  const invite = async () => {
    if (!invEmail.trim()) return;
    setInviting(true);
    try {
      const r = await apiFetch('POST', `/platform/tenants/${id}/users`, { email: invEmail.trim(), name: invName.trim(), role: invRole });
      if (!r.ok) { toast.error(((r.data as { code?: string })?.code) === 'email_taken' ? 'Este e-mail já tem conta.' : 'Falha ao cadastrar usuário.'); return; }
      toast.success('Usuário cadastrado — convite enviado por e-mail.');
      setInvEmail(''); setInvName('');
      qc.invalidateQueries({ queryKey: ['platform-tenant-users', id] });
    } finally { setInviting(false); }
  };

  // Edição de usuário (nome + papel).
  const [editing, setEditing] = useState<AccUser | null>(null);
  const [edName, setEdName] = useState('');
  const [edRole, setEdRole] = useState('agent');
  const [saving, setSaving] = useState(false);
  const openEdit = (u: AccUser) => { setEditing(u); setEdName(u.name ?? ''); setEdRole(u.role); };
  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await apiFetch('PATCH', `/platform/tenants/${id}/users/${editing.id}`, { name: edName.trim(), role: edRole });
      if (!r.ok) { toast.error(((r.data as { code?: string })?.code) === 'last_admin' ? 'A conta precisa de ao menos um administrador.' : 'Falha ao salvar.'); return; }
      toast.success('Usuário atualizado.');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['platform-tenant-users', id] });
    } finally { setSaving(false); }
  };

  const cancelDeletion = async () => {
    const r = await apiFetch('POST', `/platform/tenants/${id}/delete/cancel`);
    if (!r.ok) { toast.error('Falha ao cancelar a exclusão.'); return; }
    toast.success('Exclusão cancelada. A conta volta a funcionar.');
    qc.invalidateQueries({ queryKey: ['platform-tenant', id] });
  };

  const d = detail.data;
  const pending = !!d?.tenant.deletion_pending;
  const delMins = d?.tenant.deletion_run_after ? Math.max(0, Math.round((new Date(d.tenant.deletion_run_after).getTime() - Date.now()) / 60000)) : 0;
  const series = (d?.usage.series ?? []).map((p) => ({
    label: new Date(p.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
    sent: p.sent, received: p.received,
  }));
  const hasUsage = (d?.usage.total ?? 0) > 0;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/platform/tenants" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="size-4" /> Todas as contas
      </Link>

      {detail.isLoading ? <Skeleton className="h-24 w-full" /> : !d ? (
        <Badge variant="secondary">Conta não encontrada.</Badge>
      ) : (
        <>
          {/* Cabeçalho */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold">{d.tenant.name || '—'}</h2>
              <p className="font-mono text-xs text-muted-foreground">{d.tenant.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={d.tenant.plan === 'pro' ? 'success' : 'secondary'}>
                {d.tenant.plan === 'pro' && d.tenant.plan_comp ? `Pro cortesia até ${fmtDate(d.tenant.plan_renews_at)}` : (PLAN_LABEL[d.tenant.plan] ?? d.tenant.plan)}
              </Badge>
              {d.tenant.billing_exempt && <Badge variant="warning">Cortesia{d.tenant.billing_exempt_until ? ` até ${fmtDate(d.tenant.billing_exempt_until)}` : ''}</Badge>}
              <Badge variant="secondary">{d.tenant.currency}</Badge>
            </div>
          </div>

          {/* Bloqueio: exclusão agendada */}
          {pending && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 font-medium">Exclusão agendada — esta conta será excluída em ~{delMins} min e está <strong>bloqueada</strong> até lá.</span>
              <Button size="sm" variant="outline" onClick={cancelDeletion}>Cancelar exclusão</Button>
            </div>
          )}

          {/* Dashboard da conta */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 *:min-w-0">
            <Stat icon={CalendarDays} label="Cadastro" value={fmtDate(d.tenant.created_at)} />
            <Stat icon={Smartphone} label="Números" value={d.counts.numbers} />
            <Stat icon={Users} label="Usuários" value={d.counts.users} />
            <Stat icon={FolderKanban} label="Projetos" value={d.counts.projects} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 *:min-w-0">
            <Stat icon={CalendarDays} label="Mensagens (30d)" value={(d.usage.total ?? 0).toLocaleString('pt-BR')} />
            <Stat icon={CalendarDays} label="Enviadas" value={(d.usage.sent ?? 0).toLocaleString('pt-BR')} />
            <Stat icon={CalendarDays} label="Recebidas" value={(d.usage.received ?? 0).toLocaleString('pt-BR')} />
            <Stat icon={CalendarDays} label="Entregues" value={(d.usage.delivered ?? 0).toLocaleString('pt-BR')} />
          </div>

          {/* Tendência */}
          <Card className="min-w-0">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Mensagens por dia (30 dias)</CardTitle>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-sky-500" />Enviadas</span>
                <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-emerald-500" />Recebidas</span>
              </div>
            </CardHeader>
            <CardContent>
              {!hasUsage ? (
                <p className="py-16 text-center text-sm text-muted-foreground">Sem mensagens no período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={series} margin={{ left: -20, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="dSent" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.35} /><stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} /></linearGradient>
                      <linearGradient id="dRecv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                    <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} width={48} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--popover-foreground)', fontSize: 12 }} />
                    <Area type="monotone" dataKey="sent" name="Enviadas" stroke="#0ea5e9" strokeWidth={2} fill="url(#dSent)" />
                    <Area type="monotone" dataKey="received" name="Recebidas" stroke="#10b981" strokeWidth={2} fill="url(#dRecv)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Plano, cortesias, add-ons, limites e dados — só o admin da plataforma */}
          <TenantCommercial id={id} disabled={pending} />

          {/* Política de campanha (mínimo de números e tempo de conexão) — só o admin da plataforma edita */}
          <CampaignConfigCard
            id={id}
            initial={d.campaign_config ?? { warmup_days: 7, min_numbers: 2 }}
            onSaved={() => qc.invalidateQueries({ queryKey: ['platform-tenant', id] })}
          />

          {/* Usuários */}
          <Card className="min-w-0">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Usuários</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {/* Cadastrar usuário */}
              <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-3 sm:flex-row sm:items-end">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label className="text-xs">E-mail</Label>
                  <Input value={invEmail} onChange={(e) => setInvEmail(e.target.value)} type="email" placeholder="usuario@empresa.com" />
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label className="text-xs">Nome</Label>
                  <Input value={invName} onChange={(e) => setInvName(e.target.value)} placeholder="Nome (opcional)" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">Papel</Label>
                  <Select value={invRole} onValueChange={setInvRole}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="agent">Agente</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={invite} disabled={pending || inviting || !invEmail.trim()}><UserPlus className="size-4" /> Cadastrar</Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr><th className="p-2 font-medium">Usuário</th><th className="p-2 font-medium">Papel</th><th className="p-2 font-medium">Cadastro</th><th className="p-2 font-medium">Status</th><th className="p-2" /></tr>
                  </thead>
                  <tbody>
                    {(users.data ?? []).map((u) => (
                      <tr key={u.id} className="border-b last:border-0">
                        <td className="p-2">
                          <div className="font-medium">{u.name || '—'}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </td>
                        <td className="p-2"><Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>{u.role === 'admin' ? 'Admin' : 'Agente'}</Badge></td>
                        <td className="p-2 text-muted-foreground">{fmtDate(u.created_at)}</td>
                        <td className="p-2">{u.email_verified_at ? <Badge variant="success">Verificado</Badge> : <Badge variant="warning">Pendente</Badge>}</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost" disabled={pending} onClick={() => openEdit(u)}><Pencil className="size-4" /> Editar</Button>
                        </td>
                      </tr>
                    ))}
                    {!users.isLoading && (users.data ?? []).length === 0 && (
                      <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Nenhum usuário.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Faturas */}
          <Card className="min-w-0">
            <CardHeader><CardTitle>Faturas</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr><th className="p-2 font-medium">Nº</th><th className="p-2 font-medium">Emissão</th><th className="p-2 font-medium">Motivo</th><th className="p-2 text-right font-medium">Total</th><th className="p-2 font-medium">Status</th></tr>
                  </thead>
                  <tbody>
                    {(invoices.data ?? []).map((inv) => {
                      const st = INV_STATUS[inv.status] ?? { label: inv.status, variant: 'secondary' as const };
                      return (
                        <tr key={inv.id} className="border-b last:border-0">
                          <td className="p-2 font-mono">#{inv.number}</td>
                          <td className="p-2 text-muted-foreground">{fmtDate(inv.issue_date)}</td>
                          <td className="p-2 text-muted-foreground">{inv.reason || '—'}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{formatMoney(inv.total_cents, inv.currency)}</td>
                          <td className="p-2"><Badge variant={st.variant}>{st.label}</Badge></td>
                        </tr>
                      );
                    })}
                    {!invoices.isLoading && (invoices.data ?? []).length === 0 && (
                      <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Nenhuma fatura.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Zona de perigo — excluir a conta do cliente */}
          <Card className="border-destructive/40">
            <CardHeader className="flex-row items-center gap-2">
              <AlertTriangle className="size-4 text-destructive" />
              <CardTitle className="text-destructive">Zona de perigo</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {pending ? (
                <>
                  <p className="text-sm text-muted-foreground">Esta conta está <strong>agendada para exclusão</strong> (~{delMins} min) e bloqueada. Você pode cancelar a exclusão.</p>
                  <Button variant="outline" className="self-start" onClick={cancelDeletion}>Cancelar exclusão</Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Excluir definitivamente esta conta e todos os dados (mensagens, contatos, mídias, números, faturas). Retemos apenas o mínimo fiscal anonimizado. Ação irreversível — confirmada por um código enviado ao seu e-mail de operador.</p>
                  <Button variant="destructive" className="self-start" onClick={() => setDelOpen(true)}><Trash2 className="size-4" /> Excluir esta conta</Button>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog: editar usuário */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar usuário</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-1.5">
              <Label>E-mail</Label>
              <Input value={editing?.email ?? ''} disabled />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ed-name">Nome</Label>
              <Input id="ed-name" value={edName} onChange={(e) => setEdName(e.target.value)} placeholder="Nome" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Papel</Label>
              <Select value={edRole} onValueChange={setEdRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="agent">Agente</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteAccountDialog
        open={delOpen} onOpenChange={setDelOpen}
        base={`/platform/tenants/${id}/delete`}
        onScheduled={() => navigate('/platform/tenants')}
      />
    </div>
  );
}
